import { AttachmentService, AttachmentType, type AttachmentDetails, type AttachmentEntityType, type SasTokenDetails } from '@/services/AttachmenteService';
import { type AxiosProgressEvent } from 'axios';
import {
  FileAudio,
  FileIcon,
  FileImage,
  FileText,
  FileVideo,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/react';

type FileWithProgress = {
  id: string;
  file: File;
  progress: number;
  uploaded: boolean;
};

type FileUploadCopied = {
  onFileUploaded: (attachment: AttachmentDetails) => void;
  tenantId: string;
  siteId: string;
  entityId: string;
  entityType: AttachmentEntityType;
  buttonText?: string;
  attachmentType: AttachmentType;
};

export function FileUploadCopied({ onFileUploaded, entityId, siteId, tenantId, entityType, buttonText, attachmentType }: FileUploadCopied) {
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useUser();
  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) {
      return;
    }

    const newFiles = Array.from(e.target.files).map((file) => ({
      file,
      progress: 0,
      uploaded: false,
      id: file.name,
    }));

    setFiles([...files, ...newFiles]);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function handleUpload() {
    if (files.length === 0 || uploading) {
      return;
    }

    setUploading(true);

    try {
      const filesToUpload = [...files];
      const sasTokens = await AttachmentService.getSasTokens(filesToUpload.map((file) => file.file.name), tenantId, siteId, entityId);

      if (sasTokens.length < filesToUpload.length) {
        throw new Error('Not enough SAS tokens returned');
      }

      let nextIndex = 0;
      const concurrency = Math.min(5, filesToUpload.length);

      const uploadFile = async (fileWithProgress: FileWithProgress, sas: SasTokenDetails) => {
        try {
          await AttachmentService.uploadToSasUrl(
            sas.sasToken,
            fileWithProgress.file,
            {
              originalFilename: fileWithProgress.file.name,
              attachmentId: sas.attachmentId,
              attachmentType,
              entityType,
              source: 'Entity',
            },
            (progressEvent: AxiosProgressEvent) => {
              const progress = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 1),
              );
              setFiles((prevFiles) =>
                prevFiles.map((file) =>
                  file.id === fileWithProgress.id ? { ...file, progress } : file,
                ),
              );
            },
          );

          setFiles((prevFiles) =>
            prevFiles.filter((file) => file.id !== fileWithProgress.id)
          );

          const objectUrl = URL.createObjectURL(fileWithProgress.file);
          onFileUploaded({
            id: sas.attachmentId,
            filename: fileWithProgress.file.name,
            entityId,
            url: objectUrl,
            thumbnailUrl: objectUrl,
            size: fileWithProgress.file.size,
            uploadDate: new Date().toISOString(),
            type: attachmentType,
            originalFilename: fileWithProgress.file.name,
            dateTaken: new Date().toISOString(),
            createdByName: user?.username || user?.firstName || 'Unknown',
          });
        } catch (error) {
          console.error(error);
        }
      };

      const worker = async () => {
        while (nextIndex < filesToUpload.length) {
          const index = nextIndex++;
          await uploadFile(filesToUpload[index], sasTokens[index]);
        }
      };

      await Promise.all(Array.from({ length: concurrency }, () => worker()));
    } catch (error) {
      console.error(error);
    } finally {
      setUploading(false);
    }
  }

  function removeFile(id: string) {
    setFiles((prevFiles) => prevFiles.filter((file) => file.id !== id));
  }

  function handleClear() {
    setFiles([]);
  }

  return (
    <div className="flex flex-col gap-1">
      {/* <h2 className="text-l font-bold">File Upload</h2> */}
      <div className="flex gap-2">
        <FileInput
          inputRef={inputRef}
          disabled={uploading}
          onFileSelect={handleFileSelect}
          buttonText={buttonText}
        />
        <ActionButtons
          disabled={files.length === 0 || uploading}
          onUpload={handleUpload}
          onClear={handleClear}
        />
      </div>
      <FileList files={files} onRemove={removeFile} uploading={uploading} />
    </div>
  );
}

type FileInputProps = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  disabled: boolean;
  onFileSelect: (e: ChangeEvent<HTMLInputElement>) => void;
  buttonText?: string;
};

function FileInput({ inputRef, disabled, onFileSelect, buttonText }: FileInputProps) {
  return (
    <>
      <input
        type="file"
        ref={inputRef}
        onChange={onFileSelect}
        multiple
        className="hidden"
        id="file-upload"
        disabled={disabled}
      />
      <label
        htmlFor="file-upload"
        className="flex cursor-pointer items-center rounded-md bg-grayscale-700 py-1 border-2 border-green-500 px-1 text-sm hover:border-green-700"
      >
        <Plus size={15} />
        {buttonText}
      </label>
    </>
  );
}

type ActionButtonsProps = {
  disabled: boolean;
  onUpload: () => void;
  onClear: () => void;
};

function ActionButtons({ onUpload, onClear, disabled }: ActionButtonsProps) {
  const baseClass = "flex items-center gap-2 py-1 rounded transition-opacity duration-200 bg-green-600 px-2 text-white";
  const disabledClass = "opacity-50 cursor-not-allowed";
  const enabledClass = "hover:bg-green-700 text-sm";
  return (
    <>
      <button
        onClick={onUpload}
        disabled={disabled}
        className={`${baseClass} ${disabled ? disabledClass : enabledClass}`}
      >
        <Upload size={15} />
        Upload
      </button>
      <button
        onClick={onClear}
        disabled={disabled}
        className={`${baseClass} ${disabled ? disabledClass : enabledClass}`}
      >
        <Trash2 size={15} />
        Clear All
      </button>
    </>
  );
}

type FileListProps = {
  files: FileWithProgress[];
  onRemove: (id: string) => void;
  uploading: boolean;
};

function FileList({ files, onRemove, uploading }: FileListProps) {
  if (files.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <h4 className="font-semibold">Upload:</h4>
      <div className="space-y-2">
        {files.map((file) => (
          <FileItem
            key={file.id}
            file={file}
            onRemove={onRemove}
            uploading={uploading}
          />
        ))}
      </div>
    </div>
  );
}

type FileItemProps = {
  file: FileWithProgress;
  onRemove: (id: string) => void;
  uploading: boolean;
};

function FileItem({ file, onRemove, uploading }: FileItemProps) {
  const [imgSrc, setImgSrc] = useState<string | null>(null);

  // Only for jpg files, generate a data URL and set as imgSrc
  // This effect runs only once per file
  useEffect(() => {
    if (
      (file.file.type === 'image/jpeg' || file.file.name.toLowerCase().endsWith('.jpg') || file.file.name.toLowerCase().endsWith('.jpeg')) &&
      !imgSrc
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof e.target?.result === 'string') {
          setImgSrc(e.target.result); // Use the data URL directly
        }
      };
      reader.readAsDataURL(file.file);
    }
  }, [file.file, imgSrc]);

  return (
    <div className="space-y-1 rounded-md bg-grayscale-700 px-2">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {imgSrc ? (
            <img src={imgSrc} alt={file.file.name} style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />
          ) : (
            getFileIconElement(file.file.type)
          )}
          <div className="flex flex-col">
            <span className="font-medium text-base">{file.file.name}</span>
            <div className="flex items-center gap-2 text-xs text-grayscale-400">
              <span>{formatFileSize(file.file.size)}</span>
              <span>•</span>
              <span>{file.file.type || 'Unknown type'}</span>
            </div>
          </div>
        </div>
        <div>
        {!uploading && (
            <button onClick={() => onRemove(file.id)} className="bg-none p-1 rounded hover:bg-gray-300">
                <X size={16} className="text-black" />
            </button>
        )}
            <div className="text-right text-xs">
                {file.progress > 99 ? 'Finalising' : `${Math.round(file.progress)}%`}
            </div>
        </div>
      </div>
      <ProgressBar progress={file.progress} />
    </div>
  );
}

type ProgressBarProps = {
  progress: number;
};

function ProgressBar({ progress }: ProgressBarProps) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-grayscale-800">
      <div
        className="h-full bg-primary-500 transition-all duration-300"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

const getFileIconElement = (mimeType: string) => {
  const iconProps = { size: 50, className: "text-primary-500" };

  if (mimeType.startsWith('image/')) return <FileImage {...iconProps} />;
  if (mimeType.startsWith('video/')) return <FileVideo {...iconProps} />;
  if (mimeType.startsWith('audio/')) return <FileAudio {...iconProps} />;
  if (mimeType === 'application/pdf') return <FileText {...iconProps} />;
  return <FileIcon {...iconProps} />;
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};